// Buchhaltung — Zahlungsimport (Summen-/Saldenliste CSV) mit Auto-Zuordnung.
import { useState, useRef } from "react";
import { Loader2, UploadCloud, Check, AlertTriangle, Receipt } from "lucide-react";
import { toast } from "sonner";
import api, { formatPrice } from "../../lib/api";
import { Button } from "../../components/ui/button";

export default function PaymentsImport({ onDone }) {
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [booking, setBooking] = useState(false);
    const [fileName, setFileName] = useState("");
    const fileRef = useRef(null);

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setFileName(file.name);
        setLoading(true);
        setPreview(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/buchhaltung/payments-import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setPreview(r.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "CSV konnte nicht gelesen werden.");
        } finally {
            setLoading(false);
        }
    };

    const commit = async () => {
        const rows = (preview?.rows || []).filter((r) => r.ok);
        if (rows.length === 0) { toast.error("Keine zuordenbaren Zahlungen."); return; }
        setBooking(true);
        try {
            const r = await api.post("/buchhaltung/payments-import/commit", { rows });
            toast.success(`${r.data?.booked_count ?? 0} Zahlung(en) gebucht.`);
            setPreview(null); setFileName("");
            onDone?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Buchen fehlgeschlagen.");
        } finally {
            setBooking(false);
        }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-5 space-y-4" data-testid="buchhaltung-payments-import">
            <p className="text-xs text-gray-500">
                Importieren Sie eine <strong>Zahlungs- bzw. Summen-/Saldenliste</strong> (CSV mit Spalten <em>Rechnungsnummer</em> und <em>Betrag</em>). Die Zahlungen werden automatisch den offenen Ausgangsrechnungen zugeordnet — so lassen sich Sammelzahlungen auflösen.
            </p>
            <div className="flex items-center gap-2">
                <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="buchhaltung-payments-file" />
                <Button type="button" onClick={() => fileRef.current?.click()} className="h-9 rounded-sm bg-[#111827] text-[#FFCC00] hover:bg-black text-sm font-bold" data-testid="buchhaltung-payments-choose">
                    <UploadCloud className="w-4 h-4 mr-1.5" /> CSV wählen
                </Button>
                {fileName && <span className="text-xs text-gray-600 truncate">{fileName}</span>}
                {loading && <Loader2 className="w-4 h-4 animate-spin text-gray-500" />}
            </div>

            {preview && (
                <>
                    <div className="flex items-center gap-3 text-xs font-bold">
                        <span className="text-emerald-700 inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> {preview.matched} zuordenbar</span>
                        {preview.unmatched > 0 && <span className="text-amber-700 inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> {preview.unmatched} ohne Treffer</span>}
                        <span className="text-gray-500">Summe {formatPrice(preview.sum_amount || 0)}</span>
                    </div>
                    <div className="border border-gray-200 rounded-sm overflow-auto max-h-80">
                        <table className="w-full text-[11px]">
                            <thead className="bg-gray-100 text-gray-600 sticky top-0">
                                <tr>
                                    <th className="text-left px-2 py-1.5">Rechnungsnr.</th>
                                    <th className="text-right px-2 py-1.5">Betrag</th>
                                    <th className="text-left px-2 py-1.5">Zuordnung</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(preview.rows || []).map((r, i) => (
                                    <tr key={i} className={r.ok ? "" : "bg-amber-50"} data-testid={`buchhaltung-payment-row-${i}`}>
                                        <td className="px-2 py-1 font-mono-num">{r.invoice_number || "—"}</td>
                                        <td className="px-2 py-1 text-right font-mono-num">{r.amount != null ? formatPrice(r.amount) : "—"}</td>
                                        <td className="px-2 py-1">
                                            {r.match
                                                ? (r.match.already_paid ? <span className="text-gray-500">bereits bezahlt</span> : <span className="text-emerald-700 font-bold">{r.match.invoice_number} (offen {formatPrice(r.match.open_amount)})</span>)
                                                : <span className="text-amber-700">keine offene Rechnung gefunden</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="flex justify-end">
                        <Button type="button" onClick={commit} disabled={booking || (preview.matched || 0) === 0}
                            className="h-9 rounded-sm bg-[#111827] text-[#FFCC00] hover:bg-black text-sm font-bold" data-testid="buchhaltung-payments-commit">
                            {booking ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Receipt className="w-4 h-4 mr-1.5" />}
                            {preview.matched ? `${preview.matched} Zahlung(en) buchen` : "Buchen"}
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
}
